import { fromInteger } from '@repo/money'
import type { SocialBet } from 'src/types/domain/SocialBet'
import type { SocialBetDTO, SocialBetSnapshotItemDTO } from 'src/actions/types'
import type { RoundInfo } from 'src/types/domain/RoundInfo'
import type { BetStatus } from 'src/types/domain/BetStatus'

/**
 * ONE normalizer for BOTH social wire paths (live broadcast + snapshot).
 *
 * The socket-server creates every live social bet with status "accepted"
 * (social_bet.ex) — a value the shared BetStatusMap does not know. Routing
 * the live path through that map left status UNDEFINED, which renders like
 * pending during the round but silently dodges every `=== 'pending'` check —
 * so the previous-tab and crashed-round LOST labels never appeared until a
 * refresh rebuilt the bets through the snapshot path (which did normalize
 * 'accepted'). The two paths must agree, and an unknown status must degrade
 * to 'pending' (an unresolved-looking row), never undefined.
 */
const socialWireStatus = (status: string | undefined): BetStatus => {
  const map: Record<string, BetStatus> = {
    accepted: 'pending',
    pending: 'pending',
    won: 'won',
    lost: 'lost',
    cashed_out: 'cashed_out',
    voided: 'cancelled',
    cancelled: 'cancelled'
  }
  return (status && map[status]) || 'pending'
}

export const fromSocialBetDTO = (socialBetDTO: SocialBetDTO): SocialBet => ({
  id: socialBetDTO.bet.id,
  // playerId: socialBetDTO.playerId,
  playerDisplayName: socialBetDTO.bet.display_name,
  playerCountry: socialBetDTO.bet.country,
  raceId: socialBetDTO.bet.round_id,
  raceConfigurationId: socialBetDTO.bet.game_id,
  betAmount: fromInteger(socialBetDTO.bet.amount, 'USD'),
  betDetails: socialBetDTO.bet.details,
  status: socialWireStatus(socialBetDTO.bet.status),
  potentialPayout: fromInteger(socialBetDTO.bet.potential_payout, 'USD'),
  createdAt: socialBetDTO.bet.created_at
})

export const fromSocialBetDTOToRoundInfo = (socialBetDTO: SocialBetDTO): RoundInfo => ({
  id: socialBetDTO.bet.round_id,
  totalBets: socialBetDTO.social.total_bets,
  totalAmount: fromInteger(socialBetDTO.social.total_amount, 'USD')
})

/** Maps a snapshot item DTO (camelCase, from Mnesia) to a SocialBet domain object */
export const fromSnapshotItemDTO = (dto: SocialBetSnapshotItemDTO): SocialBet => ({
  id: dto.betId,
  playerDisplayName: dto.displayName ?? undefined,
  playerCountry: dto.country ?? undefined,
  raceId: dto.roundId,
  raceConfigurationId: dto.gameId,
  betAmount: fromInteger(dto.amount, 'USD'),
  betDetails: dto.details ?? { type: 'PickWinner', selectedItems: [] },
  status: socialWireStatus(dto.status),
  // The snapshot / outcome payloads now carry the USD potential payout, so this
  // no longer hardcodes 0. It stayed 0 for a long time because the field simply
  // did not exist on that path — which meant a reconnect REPLACED an already
  // correct live value with zero (setRoundSnapshot lets the snapshot copy win).
  //
  // 0 remains the fallback for rows cached by an older server, and it reads as
  // "unknown": the row suppresses the potential-win readout rather than showing
  // a confident $0.00. Never recompute it from stake x odds — the server figure
  // includes lightning/boost and a client-side estimate would under-report the
  // players who won most.
  potentialPayout: dto.potentialPayout ? fromInteger(dto.potentialPayout, 'USD') : 0,
  cashoutMultiplier: dto.cashoutMultiplier ?? undefined,
  actualPayout: dto.actualPayout ? fromInteger(dto.actualPayout, 'USD') : undefined,
  createdAt: dto.createdAt ?? ''
})
